import React from 'react';
import {
  Box,
  Grid,
  TextField,
  FormControl,
  Select,
  MenuItem,
  Paper,
  Typography,
  Button,
} from '@mui/material';

const Step2Pendidikan = ({ form, setForm }) => {
  // Pastikan education selalu array
  const education = Array.isArray(form.education) ? form.education : [{ period: '', schoolName: '', major: '', grade: '', city: '', certificate: '' }];
  
  // Pastikan courses selalu array
  const courses = Array.isArray(form.courses) ? form.courses : [{ period: '', courseName: '', organizer: '', certificate: '' }];

  const handleArrayChange = (setter) => (index, field, value) => {
    setter(prev => {
      // Pastikan prev selalu array
      const currentArray = Array.isArray(prev) ? prev : [];
      const newArray = [...currentArray];
      newArray[index] = { ...newArray[index], [field]: value };
      return newArray;
    });
  };

  const handleArrayRemove = (setter) => (index) => {
    setter(prev => {
      // Pastikan prev selalu array
      const currentArray = Array.isArray(prev) ? prev : [];
      return currentArray.filter((_, i) => i !== index);
    });
  };

  const addEducation = () => {
    setForm(prev => {
      // Pastikan education selalu array
      const currentEducation = Array.isArray(prev.education) ? prev.education : [];
      return {
        ...prev,
        education: [...currentEducation, { period: '', schoolName: '', major: '', grade: '', city: '', certificate: '' }]
      };
    });
  };

  const addCourses = () => {
    setForm(prev => {
      // Pastikan courses selalu array
      const currentCourses = Array.isArray(prev.courses) ? prev.courses : [];
      return {
        ...prev,
        courses: [...currentCourses, { period: '', courseName: '', organizer: '', certificate: '' }]
      };
    });
  };

  const setCourses = (updaterOrArray) => {
    // Support both functional updates (receives previous courses array)
    // and direct array assignment
    setForm(prevForm => {
      const prevCourses = Array.isArray(prevForm.courses) ? prevForm.courses : [];
      const nextCourses = typeof updaterOrArray === 'function'
        ? updaterOrArray(prevCourses)
        : updaterOrArray;
      return {
        ...prevForm,
        courses: Array.isArray(nextCourses) ? nextCourses : []
      };
    });
  };

  const setEducation = (updaterOrArray) => {
    // Support both functional updates (receives previous education array)
    // and direct array assignment
    setForm(prevForm => {
      const prevEducation = Array.isArray(prevForm.education) ? prevForm.education : [];
      const nextEducation = typeof updaterOrArray === 'function'
        ? updaterOrArray(prevEducation)
        : updaterOrArray;
      return {
        ...prevForm,
        education: Array.isArray(nextEducation) ? nextEducation : []
      };
    });
  };

  return (
    <Box sx={{ maxWidth: 1100, mx: 'auto', my: 4, p: 2 }}>
      <Paper 
        elevation={3} 
        sx={{ 
          p: 4, 
          backgroundColor: '#ffffff', 
          mb: 3,
          border: '2px solid #1976d2',
          borderRadius: 2,
          boxShadow: '0 4px 20px rgba(25, 118, 210, 0.15)'
        }}
      >
        <Grid container spacing={3}>
          {/* Header Section */}
          <Grid item xs={12}>
            <Box 
              sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                mb: 3,
                pb: 2,
                borderBottom: '2px solid #e3f2fd'
              }}
            >
              <Typography 
                variant="h5" 
                sx={{ 
                  fontWeight: 'bold', 
                  color: '#1976d2',
                  fontSize: '1.5rem',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1
                }}
              >
                <span>🎓</span>
                Data Pendidikan
              </Typography>
            </Box>
            <Typography 
              variant="body1" 
              sx={{ 
                color: '#666', 
                mb: 3,
                fontStyle: 'italic',
                fontSize: '0.95rem'
              }}
            >
              Silakan isi data pendidikan formal Anda. Klik "Tambah Pendidikan" untuk menambahkan riwayat pendidikan lainnya.
            </Typography>
          </Grid>

          {/* Education Table */}
          <Grid item xs={12}>
            <Paper elevation={1} sx={{ p: 2, backgroundColor: '#f8f9fa' }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 2, color: '#495057' }}>
                Riwayat Pendidikan Formal
              </Typography>
              
              {/* Table Header */}
              <Grid container spacing={1} sx={{ mb: 2, fontWeight: 'bold', backgroundColor: '#e3f2fd', p: 1, borderRadius: 1 }}>
                <Grid item xs={1}>No</Grid>
                <Grid item xs={2}>Tahun</Grid>
                <Grid item xs={3}>Nama Sekolah</Grid>
                <Grid item xs={2}>Jurusan</Grid>
                <Grid item xs={1}>Nilai/IPK</Grid>
                <Grid item xs={1}>Kota</Grid>
                <Grid item xs={1}>Ijasah</Grid>
                <Grid item xs={1} sx={{ textAlign: 'center' }}>
                  <Typography variant="body2" sx={{ fontSize: '0.8rem', color: '#666' }}>
                    (Pilih Jenis Ijasah)
                  </Typography>
                </Grid>
                <Grid item xs={1}>Aksi</Grid>
              </Grid>
              
              {/* Table Rows */}
              {education.map((row, idx) => (
                <Grid container spacing={1} key={`education-${idx}`} sx={{ mb: 1, alignItems: 'center', p: 1, backgroundColor: '#ffffff', borderRadius: 1 }}>
                  <Grid item xs={1}>
                    <Typography variant="body2" sx={{ fontWeight: 'bold' }}>{idx + 1}</Typography>
                  </Grid>
                  <Grid item xs={2}>
                    <TextField 
                      placeholder="Contoh: 2018-2022"
                      value={row.period} 
                      onChange={(e) => handleArrayChange(setEducation)(idx, 'period', e.target.value)} 
                      fullWidth 
                      size="small"
                      variant="outlined"
                      required
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 1,
                          '&:hover fieldset': {
                            borderColor: '#1976d2',
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: '#1976d2',
                          },
                        },
                      }}
                    />
                  </Grid>
                  <Grid item xs={3}>
                    <TextField 
                      placeholder="Nama Sekolah"
                      value={row.schoolName} 
                      onChange={(e) => handleArrayChange(setEducation)(idx, 'schoolName', e.target.value)} 
                      fullWidth 
                      size="small"
                      variant="outlined"
                      required
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 1,
                          '&:hover fieldset': {
                            borderColor: '#1976d2',
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: '#1976d2',
                          },
                        },
                      }}
                    />
                  </Grid>
                  <Grid item xs={2}>
                    <TextField 
                      placeholder="Jurusan"
                      value={row.major} 
                      onChange={(e) => handleArrayChange(setEducation)(idx, 'major', e.target.value)} 
                      fullWidth 
                      size="small"
                      variant="outlined"
                      required
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 1,
                          '&:hover fieldset': {
                            borderColor: '#1976d2',
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: '#1976d2',
                          },
                        },
                      }}
                    />
                  </Grid>
                  <Grid item xs={1}>
                    <TextField 
                      placeholder="IPK"
                      value={row.grade} 
                      onChange={(e) => handleArrayChange(setEducation)(idx, 'grade', e.target.value)} 
                      fullWidth 
                      size="small"
                      variant="outlined"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 1,
                          '&:hover fieldset': {
                            borderColor: '#1976d2',
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: '#1976d2',
                          },
                        },
                      }}
                    />
                  </Grid>
                  <Grid item xs={1}>
                    <TextField 
                      placeholder="Kota"
                      value={row.city} 
                      onChange={(e) => handleArrayChange(setEducation)(idx, 'city', e.target.value)} 
                      fullWidth 
                      size="small"
                      variant="outlined"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 1,
                          '&:hover fieldset': {
                            borderColor: '#1976d2',
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: '#1976d2',
                          },
                        },
                      }}
                    />
                  </Grid>
                  <Grid item xs={1}>
                    <Typography variant="caption" sx={{ display: 'block', mb: 0.5, fontSize: '0.75rem', color: '#666', fontWeight: 500 }}>
                      Ijasah
                    </Typography>
                    <FormControl fullWidth size="small" required>
                      <Select 
                        value={row.certificate || ''} 
                        onChange={(e) => handleArrayChange(setEducation)(idx, 'certificate', e.target.value)}
                        displayEmpty
                        required
                        sx={{ 
                          minWidth: 100,
                          '& .MuiOutlinedInput-root': {
                            borderRadius: 1,
                            '&:hover fieldset': {
                              borderColor: '#1976d2',
                            },
                            '&.Mui-focused fieldset': {
                              borderColor: '#1976d2',
                            },
                          },
                        }}
                      >
                        <MenuItem value="">-</MenuItem>
                        <MenuItem value="SD">SD</MenuItem>
                        <MenuItem value="SMP">SMP</MenuItem>
                        <MenuItem value="SMA">SMA</MenuItem>
                        <MenuItem value="SMK">SMK</MenuItem>
                        <MenuItem value="D1">D1</MenuItem>
                        <MenuItem value="D2">D2</MenuItem>
                        <MenuItem value="D3">D3</MenuItem>
                        <MenuItem value="D4">D4</MenuItem>
                        <MenuItem value="S1">S1</MenuItem>
                        <MenuItem value="S2">S2</MenuItem>
                        <MenuItem value="S3">S3</MenuItem>
                        <MenuItem value="Profesi">Profesi</MenuItem>
                        <MenuItem value="Paket A">Paket A</MenuItem>
                        <MenuItem value="Paket B">Paket B</MenuItem>
                        <MenuItem value="Paket C">Paket C</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={1}>
                    <Button 
                      size="small" 
                      onClick={() => handleArrayRemove(setEducation)(idx)}
                      variant="outlined"
                      color="error"
                      disabled={education.length === 1}
                      sx={{ minWidth: 60 }}
                    >
                      Hapus
                    </Button>
                  </Grid>
                </Grid>
              ))}
              
              <Button 
                type="button" 
                size="small" 
                onClick={addEducation}
                variant="contained"
                sx={{ 
                  mt: 2,
                  backgroundColor: '#1976d2',
                  '&:hover': {
                    backgroundColor: '#1565c0',
                  }
                }}
              >
                + Tambah Pendidikan
              </Button>
            </Paper>
          </Grid>

          {/* Courses Table */}
          <Grid item xs={12}>
            <Paper elevation={1} sx={{ p: 2, backgroundColor: '#f8f9fa' }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 2, color: '#495057' }}>
                Kursus / Pelatihan yang pernah dan sedang diikuti (Attended Courses / Training)
              </Typography>
              
              {/* Table Header */}
              <Grid container spacing={1} sx={{ mb: 2, fontWeight: 'bold', backgroundColor: '#e8f5e9', p: 1, borderRadius: 1 }}>
                <Grid item xs={1}>No</Grid>
                <Grid item xs={2}>Tahun</Grid>
                <Grid item xs={3}>Nama Kursus/Pelatihan</Grid>
                <Grid item xs={3}>Penyelenggara</Grid>
                <Grid item xs={2}>Sertifikat</Grid>
                <Grid item xs={1}>Aksi</Grid>
              </Grid>
              
              {/* Table Rows */}
              {courses.map((row, idx) => (
                <Grid container spacing={1} key={`courses-${idx}`} sx={{ mb: 1, alignItems: 'center', p: 1, backgroundColor: '#ffffff', borderRadius: 1 }}>
                  <Grid item xs={1}>
                    <Typography variant="body2" sx={{ fontWeight: 'bold' }}>{idx + 1}</Typography>
                  </Grid>
                  <Grid item xs={2}>
                    <TextField 
                      placeholder="Contoh: 2023"
                      value={row.period} 
                      onChange={(e) => handleArrayChange(setCourses)(idx, 'period', e.target.value)} 
                      fullWidth 
                      size="small"
                      variant="outlined"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 1,
                          '&:hover fieldset': {
                            borderColor: '#1976d2',
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: '#1976d2',
                          },
                        },
                      }}
                    />
                  </Grid>
                  <Grid item xs={3}>
                    <TextField 
                      placeholder="Nama Kursus/Pelatihan"
                      value={row.courseName} 
                      onChange={(e) => handleArrayChange(setCourses)(idx, 'courseName', e.target.value)} 
                      fullWidth 
                      size="small"
                      variant="outlined"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 1,
                          '&:hover fieldset': {
                            borderColor: '#1976d2',
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: '#1976d2',
                          },
                        },
                      }}
                    />
                  </Grid>
                  <Grid item xs={3}>
                    <TextField 
                      placeholder="Penyelenggara"
                      value={row.organizer} 
                      onChange={(e) => handleArrayChange(setCourses)(idx, 'organizer', e.target.value)} 
                      fullWidth 
                      size="small"
                      variant="outlined"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 1,
                          '&:hover fieldset': {
                            borderColor: '#1976d2',
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: '#1976d2',
                          },
                        },
                      }}
                    />
                  </Grid>
                  <Grid item xs={2}>
                    <TextField 
                      placeholder="Sertifikat"
                      value={row.certificate} 
                      onChange={(e) => handleArrayChange(setCourses)(idx, 'certificate', e.target.value)} 
                      fullWidth 
                      size="small"
                      variant="outlined"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 1,
                          '&:hover fieldset': {
                            borderColor: '#1976d2',
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: '#1976d2',
                          },
                        },
                      }}
                    />
                  </Grid>
                  <Grid item xs={1}>
                    <Button 
                      size="small" 
                      onClick={() => handleArrayRemove(setCourses)(idx)}
                      variant="outlined"
                      color="error"
                      disabled={courses.length === 1}
                      sx={{ minWidth: 60 }}
                    >
                      Hapus
                    </Button>
                  </Grid>
                </Grid>
              ))}
              
              <Button 
                type="button" 
                size="small" 
                onClick={addCourses}
                variant="contained"
                sx={{ 
                  mt: 2,
                  backgroundColor: '#4caf50',
                  '&:hover': {
                    backgroundColor: '#45a049',
                  }
                }}
              >
                + Tambah Kursus/Pelatihan
              </Button>
            </Paper>
          </Grid>
        </Grid>
      </Paper>
    </Box>
  );
};

export default Step2Pendidikan;
